import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserSettings, useAuthSession } from '@/api';
import type { Account } from '@/api/auth';
import { resolveChatConfig } from '@/domain/chat';
import {
  identifyChatUser,
  loadChatWidget,
  openChat,
  setChatMobileMode,
} from './chat-controller';
import { OPEN_CHAT_EVENT } from './chat-events';

/** Same cutoff as legacy `SportsApp.IsMobile` (`width < 1024`) and Tailwind `lg`. */
const MOBILE_MQ = '(max-width: 1023px)';

function readIsMobile(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(MOBILE_MQ).matches;
}

/**
 * Bootstraps the account-configured chat vendor and listens for `sportsbook:open-chat`.
 * Mount once near the app root (covers RequireLoginScreen login-only layout).
 *
 * On mobile the vendor floating button is hidden; the footer Chat tab opens the widget.
 */
export function useChatWidget(account: Account | null | undefined): void {
  const { i18n } = useTranslation();
  const { loggedIn } = useAuthSession();
  const language = (i18n.resolvedLanguage ?? i18n.language ?? 'en').split('-')[0] || 'en';
  const username = loggedIn ? (getUserSettings()?.Username?.trim() || null) : null;

  useEffect(() => {
    const config = resolveChatConfig(account);
    if (!config) return;

    let cancelled = false;
    void loadChatWidget(config, {
      language,
      username,
      isMobile: readIsMobile(),
    }).catch((err) => {
      if (!cancelled) {
        console.error('[chat] failed to load widget', err);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [account, language, username]);

  useEffect(() => {
    identifyChatUser(username);
  }, [username]);

  // Re-hide the vendor launcher whenever we are in the mobile shell breakpoint.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mql = window.matchMedia(MOBILE_MQ);
    const apply = () => {
      setChatMobileMode(mql.matches);
    };
    apply();
    mql.addEventListener('change', apply);
    return () => {
      mql.removeEventListener('change', apply);
    };
  }, [account]);

  useEffect(() => {
    const onOpen = () => {
      openChat();
    };
    window.addEventListener(OPEN_CHAT_EVENT, onOpen);
    return () => {
      window.removeEventListener(OPEN_CHAT_EVENT, onOpen);
    };
  }, []);
}
